/**
 * useManualChat (KAN-626): manual chat mode of a client dialog.
 *
 * Holds the "bot silent, operator writes" switch (optimistic, rolled back on
 * error) and sending a message by hand. Both refresh the clients list and the
 * transcript feed so the new state and the queued message show up.
 * KAN-858: setManualModeAsync resolves when the switch is saved (the manager
 * app turns manual mode on and then sends); a send refused with 409
 * manual_mode_off flips the local state off (someone returned the client to
 * the bot), so the UI offers to take the dialog again.
 */
import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { clientsApi } from '@/services/api/clients';
import { extractErrorMessage } from '@/lib/extractErrorMessage';

/** Transcript feed is polled this often while manual mode is on. */
export const MANUAL_CHAT_POLL_MS = 5000;

/** KAN-858: the send was refused because manual mode is off (409 manual_mode_off). */
export function isManualModeOffError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const response = (err as { response?: { status?: number; data?: unknown } }).response;
  if (response?.status !== 409) return false;
  const data = response.data;
  return !!data && typeof data === 'object' && (data as { code?: unknown }).code === 'manual_mode_off';
}

export interface ManualChatState {
  /** Current manual mode (optimistic). */
  manualMode: boolean;
  setManualMode: (enabled: boolean) => void;
  /** KAN-858: switch and wait; resolves true when the backend saved it. */
  setManualModeAsync: (enabled: boolean) => Promise<boolean>;
  isToggling: boolean;
  /**
   * Send text to the client; resolves true on success. KAN-688: with a photo
   * the text is its caption and may be empty.
   */
  send: (text: string, photo?: File | null) => Promise<boolean>;
  isSending: boolean;
}

interface UseManualChatOptions {
  clientId: string | null;
  /** Manual mode known by the caller; when absent it is read from the client. */
  initialManualMode?: boolean;
  /** Query key of the transcript feed to refresh. */
  transcriptKey: readonly unknown[];
  enabled: boolean;
  /**
   * KAN-641: estimate shown in the transcript; the message is recorded into it
   * (without it the backend may open a new estimate and the history is lost).
   */
  estimateId?: string | null;
  /**
   * KAN-663: our account chosen in the transcript; sent as account_id only
   * when set (without it the backend takes the account of the latest dialog).
   */
  accountId?: string | null;
  /** KAN-931: messenger of the chosen account; sent as channel unless Telegram. */
  channel?: 'telegram' | 'max' | null;
}

export function useManualChat({
  clientId,
  initialManualMode,
  transcriptKey,
  enabled,
  estimateId = null,
  accountId = null,
  channel = null,
}: UseManualChatOptions): ManualChatState {
  const queryClient = useQueryClient();
  const [manualMode, setManualModeState] = useState<boolean>(
    initialManualMode ?? false
  );

  const { data: client } = useQuery({
    queryKey: ['client', clientId],
    queryFn: () => clientsApi.getById(clientId as string),
    enabled: enabled && !!clientId && initialManualMode === undefined,
  });

  useEffect(() => {
    if (initialManualMode !== undefined) setManualModeState(initialManualMode);
  }, [initialManualMode, clientId]);

  useEffect(() => {
    if (initialManualMode === undefined && client) {
      setManualModeState(!!client.manual_mode);
    }
  }, [client, initialManualMode]);

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['clients'] });
    void queryClient.invalidateQueries({ queryKey: ['client', clientId] });
    void queryClient.invalidateQueries({ queryKey: transcriptKey });
  }, [queryClient, transcriptKey, clientId]);

  const toggle = useMutation({
    mutationFn: (value: boolean) =>
      clientsApi.setManualMode(clientId as string, value),
    onMutate: (value: boolean) => {
      const previous = manualMode;
      setManualModeState(value);
      return { previous };
    },
    onSuccess: (data) => setManualModeState(data.manual_mode),
    onError: (err, _value, context) => {
      if (context) setManualModeState(context.previous);
      toast.error(
        extractErrorMessage(err, 'Не удалось переключить ручное управление')
      );
    },
    onSettled: refresh,
  });

  const sendMutation = useMutation({
    mutationFn: ({ text, photo }: { text: string; photo: File | null }) => {
      // KAN-931: channel only for a non-Telegram account (Telegram is the
      // backend default), so Telegram calls keep their old shape.
      if (channel && channel !== 'telegram') {
        return clientsApi.sendMessage(
          clientId as string,
          text,
          estimateId,
          accountId,
          photo,
          channel
        );
      }
      return photo
        ? clientsApi.sendMessage(clientId as string, text, estimateId, accountId, photo)
        : clientsApi.sendMessage(clientId as string, text, estimateId, accountId);
    },
    onError: (err) => {
      if (isManualModeOffError(err)) setManualModeState(false);
      toast.error(extractErrorMessage(err, 'Не удалось отправить сообщение'));
    },
    onSettled: refresh,
  });

  const setManualMode = useCallback(
    (value: boolean) => {
      if (!clientId) return;
      toggle.mutate(value);
    },
    [clientId, toggle]
  );

  const setManualModeAsync = useCallback(
    async (value: boolean): Promise<boolean> => {
      if (!clientId) return false;
      try {
        await toggle.mutateAsync(value);
        return true;
      } catch {
        return false;
      }
    },
    [clientId, toggle]
  );

  const send = useCallback(
    async (text: string, photo: File | null = null): Promise<boolean> => {
      const trimmed = text.trim();
      if (!clientId || (!trimmed && !photo)) return false;
      try {
        await sendMutation.mutateAsync({ text: trimmed, photo });
        return true;
      } catch {
        return false;
      }
    },
    [clientId, sendMutation]
  );

  return {
    manualMode,
    setManualMode,
    setManualModeAsync,
    isToggling: toggle.isPending,
    send,
    isSending: sendMutation.isPending,
  };
}
